import React, { FC, useCallback, useEffect, useState } from 'react'
import { Field, FieldProps } from "formik"
import { Form, Formik, FormikProps } from 'formik'
import {
    IGeneralExpenseModel,
    validationGeneralExpense,
    initialGeneralExpense,
    tabOptions,
    currencyOptions,
    expenseOptions,
    paymentTypeOptions,
    cashCodeOptions,
    ibanOptions,
} from './GeneralExpenseModel'
import { createGeneralExpense, deleteGeneralExpense, loadGeneralExpenses, updateGeneralExpense } from './Api'
import { ConfirmModal } from '../../../shared/components/ConfirmModal'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import FormTabHeader from '../../../shared/components/FormTabHeader'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import { showErrorToast, showSuccessToast } from '../../../shared/components/ToastUtils'


export const GeneralExpensePage: FC = () => {
    const [tab, setTab] = useState<string | 'form'>('form')
    const [locations, setLocations] = useState<IGeneralExpenseModel[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)



    const fetchLocations = useCallback(
        async (page: number = 0) => {
            try {
                const res = await loadGeneralExpenses(page)
                const data = res.data.payload || res.data
                setLocations(data.content || data || [])
                setTotalPages(data.totalPages || 1)
            } catch (err) {
                const error = err as any
                if (error.response?.status === 401) {
                    window.location.href = '/login'
                }
                console.error(error)
                showErrorToast('Genel gider yüklenirken bir hata oluştu')
            }
        },
        []
    )

    useEffect(() => {
        fetchLocations(currentPage)
    }, [fetchLocations, currentPage])

    const handleSave = async (values: IGeneralExpenseModel, resetForm: () => void) => {
        setLoading(true)
        try {
            if (selectedId) {
                await updateGeneralExpense({ ...values, id: selectedId })
                showSuccessToast('Genel gider başarıyla güncellendi')
            } else {
                await createGeneralExpense(values)
                showSuccessToast('Genel gider başarıyla oluşturuldu')
            }
            setCurrentPage(0)
            await fetchLocations(0)
            resetForm()
            setSelectedId(null)
            setTab('from');
        } catch (err) {
            showErrorToast('Bir hata oluştu')
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = (loc: IGeneralExpenseModel, formik: FormikProps<IGeneralExpenseModel>) => {
        formik.setValues(loc)
        setSelectedId(loc.id)
        setTab('form')
    }

    const handleDelete = async () => {
        if (selectedId) {
            try {
                await deleteGeneralExpense(selectedId)
                showSuccessToast('Genel gider başarıyla silindi')
                await fetchLocations(currentPage)
            } catch (err) {
                console.error(err)
                showErrorToast('Genel gider silinirken bir hata oluştu')
            } finally {
                setShowModal(false)
                setSelectedId(null)
            }
        }
    }

    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage)
        }
    }

    return (
        <>
            <Formik
                initialValues={initialGeneralExpense}
                validationSchema={validationGeneralExpense}
                enableReinitialize
                onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
            >
                {(formik) => {
                    const handleReset = () => {
                        formik.resetForm();
                        setSelectedId(null);
                    };

                    return (
                        <>
                            <FormTabHeader
                                formId='generalExpenseForm'
                                formikSubmit={formik.handleSubmit}
                                formikReset={handleReset}
                                loading={loading}
                                tab={tab}
                                setTab={setTab}
                                setSelectedId={setSelectedId}
                                tabs={tabOptions}
                                onExportExcel={() => console.log('Excel export')}
                            />

                            {tab === 'form' && (
                                <div className="card mb-5">
                                    <Form id="generalExpenseForm" className="form card-body">

                                        {/* ===== GİDER İŞLEMLERİ ===== */}
                                        <div className="d-flex align-items-center mb-4">
                                            <i className="bi bi-droplet me-2 fs-3 text-dark" />
                                            <span className="fw-bold fs-3 text-dark">Gider İşlemleri</span>
                                        </div>

                                        <div className="card shadow-sm mb-5 border border-2 border-gray-300">
                                            <div className="card-body bg-light">
                                                <div className="row g-4">
                                                    <TextInput
                                                        name="date"
                                                        label="Tarih"
                                                        type="date"
                                                        wrapperClassName="col-md-4"
                                                    />
                                                    <TextInput
                                                        name="amount"
                                                        label="Tutar"
                                                        type="number"
                                                        step="0.01"
                                                        wrapperClassName="col-md-4"
                                                    />
                                                    <TextInput
                                                        name="tax"
                                                        label="Vergi"
                                                        type="number"
                                                        step="0.01"
                                                        wrapperClassName="col-md-4"
                                                    />

                                                    <SelectInput
                                                        name="expenseName"
                                                        label="Gider Adı"
                                                        options={expenseOptions}
                                                        wrapperClassName="col-md-6"
                                                    />
                                                    <SelectInput
                                                        name="branch"
                                                        label="İşlem Şube"
                                                        options={[
                                                            { value: 'ANKARA_CANKAYA', label: 'ANKARA ÇANKAYA' },
                                                            { value: 'ISTANBUL_MERKEZ', label: 'İSTANBUL MERKEZ' },
                                                        ]}
                                                        wrapperClassName="col-md-6"
                                                    />
                                                    <TextInput
                                                        name="documentNumber"
                                                        label="Evrak No"
                                                        wrapperClassName="col-md-6"
                                                    />
                                                    <TextInput
                                                        name="operator"
                                                        label="İşlem Yapan"
                                                        wrapperClassName="col-md-6"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* ===== ÖDEME BİLGİSİ ===== */}
                                        <div className="d-flex align-items-center mb-4">
                                            <i className="bi bi-wallet2 me-2 fs-3 text-dark" />
                                            <span className="fw-bold fs-3 text-dark">Ödeme Bilgisi</span>
                                        </div>

                                        <div className="card shadow-sm mb-5 border border-2 border-gray-300">
                                            <div className="card-body bg-light">
                                                <div className="row g-4">
                                                    <TextInput
                                                        name="paymentDate"
                                                        label="Ödeme Tarihi"
                                                        type="date"
                                                        wrapperClassName="col-md-4"
                                                    />
                                                    <TextInput
                                                        name="paymentAmount"
                                                        label="Ödeme"
                                                        type="number"
                                                        step="0.01"
                                                        wrapperClassName="col-md-4"
                                                    />
                                                    <SelectInput
                                                        name="paymentType"
                                                        label="Ödeme Türü"
                                                        options={paymentTypeOptions}
                                                        wrapperClassName="col-md-4"
                                                    />
                                                    <SelectInput
                                                        name="cashCode"
                                                        label="Kasa Kodu"
                                                        options={cashCodeOptions}
                                                        wrapperClassName="col-md-6"
                                                    />
                                                    <SelectInput
                                                        name="iban"
                                                        label="IBAN ( Hesap No )"
                                                        options={ibanOptions}
                                                        wrapperClassName="col-md-6"
                                                    />
                                                    <TextAreaInput
                                                        name="description"
                                                        label="Açıklama"
                                                        rows={3}
                                                        wrapperClassName="col-12"
                                                    />
                                                    <TextInput
                                                        name="contractNumber"
                                                        label="Sözleşme No"
                                                        wrapperClassName="col-md-6"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* ===== CARİ BİLGİSİ ===== */}
                                        <div className="d-flex align-items-center mb-4">
                                            <i className="bi bi-people-fill me-2 fs-3 text-dark" />
                                            <span className="fw-bold fs-3 text-dark">Cari Bilgisi</span>
                                        </div>

                                        <div className="card shadow-sm mb-3 border border-2 border-gray-300">
                                            <div className="card-body bg-light">
                                                <div className="row g-4">
                                                    <TextInput
                                                        name="customer"
                                                        label="Cari Bilgi"
                                                        wrapperClassName="col-md-6"
                                                    />

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-bold">Dosya Bilgi</label>
                                                        <Field name="filePath">
                                                            {({ form }: FieldProps) => (
                                                                <input
                                                                    type="file"
                                                                    className="form-control"
                                                                    onChange={(e) => {
                                                                        const f = e.currentTarget.files?.[0] ?? null;
                                                                        form.setFieldValue("filePath", f ? f.name : "");
                                                                    }}
                                                                />
                                                            )}
                                                        </Field>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </Form>

                                </div>

                            )}

                            {tab === 'list' && (
                                <div className='card'>
                                    <div className='card-body'>


                                        {/* Sayfalama Kontrolleri */}
                                        <div className='d-flex justify-content-between align-items-center mt-5'>
                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={currentPage === 0}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                Önceki
                                            </button>

                                            <span>
                                                Sayfa {currentPage + 1} / {totalPages}
                                            </span>

                                            <button
                                                className='btn btn-sm btn-light'
                                                disabled={currentPage >= totalPages - 1}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                Sonraki
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                }
                }
            </Formik >

            <ConfirmModal
                show={showModal}
                header='Silme Onayı'
                text='Bu lokasyonu silmek istediğinize emin misiniz?'
                handleClose={() => {
                    setShowModal(false)
                    setSelectedId(null)
                }}
                applyOnClick={handleDelete}
            />
            <ToastContainer
                position='top-right'
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    )
}
